import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import { getAccountType } from "../utility/accountType";
import { getShortlistIds, addToShortlist, removeFromShortlist } from "../services/ShortlistService";

const ShortlistContext = createContext(null);

// Shortlist is server-backed (unlike Compare, which is localStorage-only) since it's
// meant to follow a student across devices from their dashboard. Only students get a
// populated set; everyone else sees an always-empty, read-only shortlist.
export const ShortlistProvider = ({ children }) => {
  const [ids, setIds] = useState(() => new Set());
  const isStudent = Boolean(Cookies.get("token")) && getAccountType() === "student";

  useEffect(() => {
    if (!isStudent) {
      setIds(new Set());
      return;
    }
    getShortlistIds()
      .then((res) => setIds(new Set((res.data || []).map(String))))
      .catch(() => {});
  }, [isStudent]);

  const isShortlisted = useCallback((id) => ids.has(String(id)), [ids]);

  const toggleShortlist = useCallback(
    async (college) => {
      if (!isStudent || !college?.id) return;
      const id = String(college.id);
      const alreadyIn = ids.has(id);

      // Optimistic update — the toggle should feel instant on a card grid.
      setIds((prev) => {
        const next = new Set(prev);
        alreadyIn ? next.delete(id) : next.add(id);
        return next;
      });

      try {
        if (alreadyIn) {
          await removeFromShortlist(college.id);
        } else {
          await addToShortlist(college.id);
        }
      } catch {
        // Revert on failure.
        setIds((prev) => {
          const next = new Set(prev);
          alreadyIn ? next.add(id) : next.delete(id);
          return next;
        });
      }
    },
    [ids, isStudent]
  );

  const value = useMemo(
    () => ({ isShortlisted, toggleShortlist, canShortlist: isStudent, count: ids.size }),
    [isShortlisted, toggleShortlist, isStudent, ids]
  );

  return <ShortlistContext.Provider value={value}>{children}</ShortlistContext.Provider>;
};

export const useShortlist = () => {
  const ctx = useContext(ShortlistContext);
  if (!ctx) throw new Error("useShortlist must be used within a ShortlistProvider");
  return ctx;
};
